import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useDeferredValue } from "react";
import debounce from "lodash/debounce";
import { BaseballTable } from "../components/tables/BaseballTable";
import { DataControls } from "../components/data/DataControls";
import ErrorDisplay from "../components/alerts/ErrorDisplay";
import { fetchAPI } from "../config/api";
import { getGutsColumns } from "../config/gutsColumns";
import { getParkFactorsColumns } from "../config/parkFactorsColumns";
import { getExpectedRunsColumns } from "../config/expectedRunsColumns";
import { getErrorMessage } from "../utils/errorUtils";
import { DEFAULT_YEAR, DEFAULT_DIVISION } from "../config/constants";

const ENDPOINT_MAP = {
  guts: "/api/guts",
  park_factors: "/api/park_factors",
  expected_runs: "/api/expected_runs",
};

const COLUMN_MAP = {
  guts: getGutsColumns,
  park_factors: getParkFactorsColumns,
  expected_runs: getExpectedRunsColumns,
};

const BackgroundDecoration = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-[6%] right-[8%] w-[380px] h-[380px] bg-gradient-to-r from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-[12%] left-[6%] w-[520px] h-[520px] bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse delay-1000" />
    <div className="absolute top-[48%] right-[28%] w-[300px] h-[300px] bg-gradient-to-r from-cyan-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse delay-500" />
    <div className="absolute top-[18%] left-[18%] w-[220px] h-[220px] bg-gradient-to-r from-indigo-400/25 to-purple-400/25 rounded-full blur-xl animate-pulse delay-700" />
  </div>
);

const InfoBanner = () => (
  <div className="relative overflow-hidden rounded-2xl border border-white/30 bg-white/60 backdrop-blur p-4 sm:p-5 shadow-xl">
    <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl" />
    <div className="relative z-10 flex items-start gap-3 sm:gap-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold flex-shrink-0">
        i
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm sm:text-base font-semibold text-gray-800 mb-1 truncate">
          Advanced Analytics & Metrics
        </div>
        <div className="text-xs sm:text-sm text-gray-600">
          Explore comprehensive team and player analytics including division-level constants, park factors, and expected run matrices.
        </div>
      </div>
    </div>
  </div>
);

const Guts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataType, setDataType] = useState(searchParams.get("dataType") || "guts");
  const [selectedYears, setSelectedYears] = useState([DEFAULT_YEAR]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [division, setDivision] = useState(Number(searchParams.get("division")) || DEFAULT_DIVISION);
  const [conference, setConference] = useState(searchParams.get("conference") || "");
  const [conferences, setConferences] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showError, setShowError] = useState(false);

  const deferredSearch = useDeferredValue(searchTerm);
  const dataAbortRef = useRef(null);
  const mounted = useRef(false);

  // Build API params based on data type
  const buildApiParams = useCallback(() => {
    const params = new URLSearchParams();
    
    if (dataType === "park_factors") {
      params.set("division", division.toString());
      if (conference) params.set("conference", conference);
    } else if (dataType === "expected_runs") {
      params.set("division", division.toString());
      params.set("year", selectedYears[0]?.toString() || DEFAULT_YEAR.toString());
    } else if (dataType === "guts" && selectedYears[0]) {
      params.set("year", selectedYears[0].toString());
    }
    
    return params;
  }, [dataType, division, conference, selectedYears]);

  // Fetch data
  const fetchData = useCallback(async () => {
    dataAbortRef.current?.abort();
    const controller = new AbortController();
    dataAbortRef.current = controller;
    
    setLoading(true);
    setError(null);
    setShowError(false);
    
    try {
      const params = buildApiParams();
      const endpoint = ENDPOINT_MAP[dataType] || ENDPOINT_MAP.guts;
      const url = params.toString() ? `${endpoint}?${params}` : endpoint;
      
      const result = await fetchAPI(url, { signal: controller.signal });
      const rows = Array.isArray(result) ? result : [];
      
      if (!controller.signal.aborted) {
        setData(rows);
        setError(null);
        setShowError(false);
        setIsInitialized(true);
      }
    } catch (err) {
      if (err.name !== "AbortError" && !controller.signal.aborted) {
        console.error('Error fetching data:', err);
        setError(getErrorMessage(err, { division, dataType }));
        setShowError(true);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [dataType, buildApiParams, division]);

  // Update URL params
  const updateParams = useMemo(
    () =>
      debounce((state) => {
        const params = new URLSearchParams();
        params.set("dataType", state.dataType);
        params.set("division", state.division.toString());
        params.set("search", state.searchTerm);
        if (state.conference) params.set("conference", state.conference);

        const newParams = params.toString();
        if (newParams !== searchParams.toString()) {
          setSearchParams(params);
        }
      }, 300),
    [setSearchParams, searchParams]
  );

  useEffect(() => {
    if (dataType === "park_factors" && division) {
      fetchAPI(`/api/conferences?division=${division}`)
        .then((response) => {
          if (Array.isArray(response)) setConferences(response);
        })
        .catch((err) => {
          console.error('Error fetching conferences:', err);
          setConferences([]);
        });
    } else {
      setConferences([]);
    }
  }, [dataType, division]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    updateParams({ dataType, division, searchTerm, conference });
  }, [dataType, division, searchTerm, conference, updateParams]);

  useEffect(() => {
    if (!mounted.current) return;
    fetchData();
  }, [dataType, division, searchTerm, selectedYears, conference, fetchData]);

  useEffect(() => {
    return () => {
      dataAbortRef.current?.abort();
    };
  }, []);

  // Filter data by search term
  const filteredData = useMemo(() => {
    if (!deferredSearch) return data;
    
    const search = deferredSearch.toLowerCase();
    return data.filter((item) => {
      if (!item || typeof item !== 'object') return false;
      const teamName = (item.team_name || item.team || item.name || "").toLowerCase();
      const year = (item.year || item.Year || "").toString();
      return teamName.includes(search) || year.includes(search);
    });
  }, [data, deferredSearch]);

  // Get columns for current data type
  const columns = useMemo(() => {
    const getColumns = COLUMN_MAP[dataType] || COLUMN_MAP.guts;
    return getColumns();
  }, [dataType]);

  // Clean data for table
  const tableData = useMemo(() => {
    return Array.isArray(filteredData) 
      ? filteredData.filter(item => item && typeof item === 'object')
      : [];
  }, [filteredData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <BackgroundDecoration />
      
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <div className="relative z-10 mb-6">
          <InfoBanner />
        </div>
        
        <div id="controls" className="relative z-10 mb-6">
          <DataControls
            dataType={dataType}
            selectedYears={selectedYears}
            minPA={0}
            minIP={0}
            searchTerm={searchTerm}
            conference={conference}
            division={division}
            selectedListId=""
            setDataType={setDataType}
            setSelectedYears={setSelectedYears}
            setMinPA={() => {}}
            setMinIP={() => {}}
            setSearchTerm={setSearchTerm}
            setConference={setConference}
            setDivision={setDivision}
            setSelectedListId={() => {}}
            conferences={conferences}
            allowedDataTypes={["guts", "park_factors", "expected_runs"]}
            onApplyFilters={() => {}}
          />
        </div>
        
        <div className="relative z-10">
          {!isInitialized ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-600">Initializing...</p>
            </div>
          ) : error && showError ? (
            <div className="text-center py-12">
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl p-6 max-w-2xl mx-auto">
                <ErrorDisplay 
                  error={{ message: error, status: 0 }} 
                  context={{ division, dataType }} 
                  onRetry={fetchData} 
                  onSwitchToDivision3={null} 
                />
              </div>
            </div>
          ) : (
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Loading data...</p>
                  </div>
                </div>
              )}
              
              <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
                {tableData.length === 0 && !loading ? (
                  <div className="text-center py-6 sm:py-12">
                    <p className="text-gray-500 text-base sm:text-lg">
                      No data found for the current filters.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <BaseballTable 
                      data={tableData} 
                      columns={columns} 
                      filename={`${dataType}_${division}.csv`} 
                      stickyColumns={[0, 1]} 
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Guts);
